import cn from "classnames";
import { useTranslation } from "react-i18next";
import {
  useRef,
  useEffect,
  useState,
  Suspense,
  useCallback,
  useMemo,
} from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { Header } from "features/MainPage/Header";
import { MobileMenu } from "features/MainPage/MobileMenu";
import { Footer } from "features/MainPage/Footer";
import { Text } from "shared/components/Text";
import { Button } from "shared/components/Button";
import firstShadow from "assets/images/shadow-1.png";
import secondShadow from "assets/images/shadow-2.png";
import backgroundVideo from "assets/videos/background-video-placeholder.mp4";
import mobileBackgroundVideo from "assets/videos/mobile-background-video.mp4";
import { defaultMapOptions } from "shared/constants";
import {
  createResultsByMilisecondsListener,
  getTeamData,
} from "shared/database";
import { databaseStore } from "store/database";
import { getResultsData, getTeamsData } from "store/database/selectors";
import { setIsLoading } from "store/database/actions";
import { getLocations } from "features/LocationsPage/locations";

import { GoogleMaps } from "./GoogleMaps";
import { AboutSection } from "./sections/AboutSection";
import { PriceListSection } from "./sections/PriceListSection";
import { CareerSection } from "./sections/CareerSection";
import { CooperationSection } from "./sections/CooperationSection";
import { FAQSection } from "./sections/FAQSection";
import { RankingSection } from "./sections/RankingSection";
import { ContactSection } from "./sections/ContactSection";
import css from "./styles.module.scss";
import { Toast } from "./Toast";
import { ReviewSection } from "./sections/ReviewSection";
import { ResetLocationButton } from "shared/components/ResetLocationButton";
import { LanguageChooser } from "shared/components/LanguageChooser";

const isIos = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "MacIntel",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const MAIN_PAGE_GAME_ID = "pr_i_14x28";
const MAIN_PAGE_ENTRY_COUNT = 4;

export const MainPage = () => {
  const { t, i18n } = useTranslation();

  const { city, reservationStatus } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const locations = useMemo(() => {
    return getLocations();
  }, [i18n.language]);

  const currentCityConfig = useMemo(() => {
    return locations[city];
  }, [city]);

  const location = useLocation();

  const navigate = useNavigate();

  const [mobileMenuVisibility, setMobileMenuVisibility] = useState();
  const [toastText, setToastText] = useState("");
  const [requestFailed, setRequestFailed] = useState(false);
  const [mapOptions, setMapOptions] = useState(defaultMapOptions);
  const mainPageGameResults = databaseStore.useState(
    getResultsData(MAIN_PAGE_GAME_ID),
  );
  const teamsData = databaseStore.useState(getTeamsData);

  const homeRef = useRef();
  const aboutRef = useRef();
  const priceListRef = useRef();
  const careerRef = useRef();
  const cooperationRef = useRef();
  const contactRef = useRef();
  const rankingRef = useRef();
  const faqRef = useRef();
  const reviewsRef = useRef();

  useEffect(() => {
    const bookingId = searchParams.get("booking_id");
    const personId = searchParams.get("person_id");

    const cancelReservation = async () => {
      const response = await fetch(
        `https://xl.plan4u.pl/pixel/xl/api/v1/o_cancelBooking/${bookingId}/${personId}`,
      );
      const json = await response.json();
      if (json.status === "success") {
        setToastText(t("shared.cancelled"));
        setRequestFailed(false);
      } else {
        setToastText(t("shared.cancelError"));
        setRequestFailed(true);
      }
    };
    if (bookingId && personId) {
      cancelReservation();
    }
  }, [searchParams]);

  useEffect(() => {
    if (reservationStatus === "success") {
      setToastText(t("shared.success"));
    }
  }, [reservationStatus]);

  useEffect(() => {
    switch (location.hash) {
      case "#/ranking":
        navigate("/ranking");
        break;
      case "#/booking":
        navigate("/booking");
        break;
    }
  }, [location.hash]);

  useEffect(() => {
    // checking if currentCityConfig would be undefined
    if (!locations[city]) {
      navigate("/");
      return;
    }

    homeRef.current.scrollIntoView();
    document.title = currentCityConfig.siteTitle;

    const copiedMapOptions = { ...defaultMapOptions };
    copiedMapOptions.options.zoom = currentCityConfig.googleMapsOptions.zoom;
    copiedMapOptions.options.center.lat =
      currentCityConfig.googleMapsOptions.latitude;
    copiedMapOptions.options.center.lng =
      currentCityConfig.googleMapsOptions.longitude;
    copiedMapOptions.markerOptions.position.lat =
      currentCityConfig.googleMapsOptions.latitude;
    copiedMapOptions.markerOptions.position.lng =
      currentCityConfig.googleMapsOptions.longitude;
    setMapOptions(copiedMapOptions);
  }, [currentCityConfig, navigate]);

  useEffect(() => {
    const fetchMainPageResults = async () => {
      setIsLoading(true);
      await createResultsByMilisecondsListener({
        gameId: MAIN_PAGE_GAME_ID,
        count: MAIN_PAGE_ENTRY_COUNT,
      });
      setIsLoading(false);
    };

    const fetchMainPageTeams = async () => {
      setIsLoading(true);
      for (let i = 0; i < mainPageGameResults.length; ++i) {
        await getTeamData({ teamId: mainPageGameResults[i].team_id });
      }
      setIsLoading(false);
    };

    if (!mainPageGameResults) {
      fetchMainPageResults();
    } else if (!Object.keys(teamsData).length) {
      fetchMainPageTeams();
    }
  }, [mainPageGameResults]);

  const sections = useMemo(() => {
    return [
      { title: t("header.home"), ref: homeRef },
      { title: t("header.aboutUs"), ref: aboutRef },
      { title: t("header.priceList"), ref: priceListRef },
      { title: t("header.vouchers"), url: `/${city}/vouchers` },
      { title: t("header.cooperation"), ref: cooperationRef },
      { title: t("header.ranking"), ref: rankingRef },
      { title: t("header.reviews"), ref: reviewsRef },
      { title: t("header.faq"), ref: faqRef },
      { title: t("header.career"), ref: careerRef },
      { title: t("header.contact"), ref: contactRef },
    ];
  }, [i18n.language]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const selectSection = (title, ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = useCallback(
    (event) => {
      for (let i = sections.length - 1; i >= 0; --i) {
        if (!sections[i].ref) {
          continue;
        }
        const current = sections[i].ref.current;

        if (
          current &&
          current.getBoundingClientRect().top <
            Math.round(window.innerHeight / 2)
        ) {
          setSelectedSection(sections[i].title);
          break;
        }
      }
    },
    [sections],
  );

  const homeContent = t("homeSection.content", {
    returnObjects: true,
  });

  const [selectedSection, setSelectedSection] = useState(sections[0].title);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const isMobile = window.matchMedia("(max-width: 820px)").matches;
  const shouldIosImageBeVertical =
    window.matchMedia("(max-width: 760px)").matches;

  if (!currentCityConfig) return <></>;

  return (
    <div className={css.MainPage}>
      <Header
        sections={sections}
        selectedSection={selectedSection}
        selectSection={selectSection}
        setMobileMenuVisibility={setMobileMenuVisibility}
      />
      <div className={css.languageChooserContainer}>
        <LanguageChooser
          className={css.languageChooser}
          shouldScrollUp
          borderColor="blue"
        />
      </div>
      {toastText && <Toast text={toastText} failed={requestFailed} />}
      {isFinite(mobileMenuVisibility) && (
        <MobileMenu
          mobileMenuVisibility={mobileMenuVisibility}
          setMobileMenuVisibility={setMobileMenuVisibility}
          sections={sections}
          selectedSection={selectedSection}
          selectSection={selectSection}
          cityName={currentCityConfig.title}
        />
      )}
      <div ref={homeRef} className={css.home}>
        <div
          className={cn(css.homeBackground, { [css.hide]: !isVideoLoading })}
        >
          {!isMobile && <div className={css.spinner} />}
        </div>
        {isIos() ? (
          <div className={css.iframeContainer}>
            <iframe
              src={
                isMobile
                  ? "https://player.vimeo.com/video/772344269?muted=1&autoplay=1&loop=1&title=0&byline=0&badge=0&autopause=0"
                  : "https://player.vimeo.com/video/772339493?muted=1&autoplay=1&loop=1&title=0&byline=0&badge=0&autopause=0"
              }
              data-read="true"
              frameBorder="0"
              allow="autoplay"
              // allowFullScreen
              className={css.homeVideo}
              onCanPlay={() => setIsVideoLoading(false)}
            ></iframe>
            <div className={css.iframeBlend} />
          </div>
        ) : (
          <video
            loop
            autoPlay
            muted
            className={css.homeVideo}
            playsInline
            // poster={backgroundPlaceholder}
            onCanPlay={() => setIsVideoLoading(false)}
          >
            <source
              src={isMobile ? mobileBackgroundVideo : backgroundVideo}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        )}
        <div className={css.homeContentContainer}>
          <Text
            type="white"
            className={css.homeContentTitle}
            secondaryFamily
            bold
          >
            {t(`homeSection.title.${city}`)}
          </Text>
          {homeContent.map((el) => (
            <Text key={el} type="lightBlue" className={css.homeContent}>
              {el}
            </Text>
          ))}
          <Button
            text={t("shared.buyTicket")}
            onClick={() => {
              navigate(`/${city}/booking`);
            }}
          />
          <div className={css.resetLocationButtonContainer}>
            <ResetLocationButton
              cityName={currentCityConfig.title}
              className={css.resetLocationButton}
              textClassName={css.resetLocationButtonText}
              locationPinClassName={css.resetLocationButtonPin}
            />
          </div>
        </div>
      </div>
      <div className={css.relativeContainer}>
        <div className={css.mainContent}>
          <img
            title="Shadow decoration"
            src={secondShadow}
            alt="second shadow decoration"
            className={css.secondShadow}
          />
          <AboutSection
            passedRef={aboutRef}
            images={currentCityConfig.aboutUsImages}
            mobileImages={currentCityConfig.aboutUsMobileImages}
          />
          <PriceListSection
            passedRef={priceListRef}
            prices={currentCityConfig.prices}
            bookingUrl={`/${city}/booking`}
          />
          <CooperationSection passedRef={cooperationRef} city={city} />
          <RankingSection
            passedRef={rankingRef}
            mainPageGameId={MAIN_PAGE_GAME_ID}
            mainPageEntryCount={MAIN_PAGE_ENTRY_COUNT}
            city={city}
          />
          <ReviewSection passedRef={reviewsRef} />
          <FAQSection passedRef={faqRef} />
          <CareerSection passedRef={careerRef} />
        </div>
        <ContactSection
          passedRef={contactRef}
          address={currentCityConfig.address}
        />
        <img
          title="Shadow decoration"
          src={firstShadow}
          alt="first shadow decoration"
          className={css.firstShadow}
        />
      </div>
      <GoogleMaps {...mapOptions} />
      <Footer
        scrollCallback={() => selectSection(sections[0].title, homeRef)}
      />
    </div>
  );
};
